import { useInfiniteExternalIntegrationActivities } from '@Queries/index';
import * as Api from '@ViewModels';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../../../../../web/components-v2/Collapsible';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '@WebComponents/DataGrid';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { Popover, PopoverContent, PopoverTrigger } from '@HoudiniUI/Popover';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { copyToClipboard } from '../../../../../../../models/UiUtils';
import { Button, SelectButton } from '../../../../../../../web/components-v2/Button';
import { Calendar } from '../../../../../../../web/components-v2/Calendar';
import { ClipboardIcon } from '../../../../../../../web/components/svgs/icons/ClipboardIcon';
import { DisclosureIcon } from '../../../../../../../web/components/svgs/icons/DisclosureIcon';
import { InfoIcon } from '../../../../../../../web/components/svgs/icons/InfoIcon';
import { SimpleTooltip, TooltipProvider } from '../../../../../../../web/components/Tooltip';
import { baseStyleSheet } from '../../../../../../../web/styles/styles';
import { styleSheet } from '../../styles';

const DATE_FORMAT = 'MM/DD';

const DATE_RANGE_PRESETS = [7, 14, 30, 60, 90] as const;

/** Canonical display order for ETL stage types (top to bottom within a run). */
const STAGE_ORDER: Record<string, number> = {
	PreProcess: 0,
	Extract: 1,
	Transform: 2,
	Load: 3,
};

const stageSort = (a: Api.ProcessedStatsTypes, b: Api.ProcessedStatsTypes) => {
	const aLabel = stageLabelOf(a);
	const bLabel = stageLabelOf(b);
	return (STAGE_ORDER[aLabel] ?? 99) - (STAGE_ORDER[bLabel] ?? 99);
};

const stageLabelOf = (item: Api.ProcessedStatsTypes) => (item._type ?? '').replace('ProcessedStats', '');

interface RunGroup {
	runId: string;
	items: Api.ProcessedStatsTypes[];
	earliestStart?: Date;
	latestEnd?: Date;
}

interface PartitionedActivity {
	runs: RunGroup[];
	ungrouped: Api.ProcessedStatsTypes[];
}

/**
 * Splits flat activity items into runs (grouped by `runId`) and legacy ungrouped items
 * (records emitted before the API started populating `runId`).
 */
const partitionActivity = (items: Api.ProcessedStatsTypes[]): PartitionedActivity => {
	const grouped = new Map<string, Api.ProcessedStatsTypes[]>();
	const ungrouped: Api.ProcessedStatsTypes[] = [];

	for (const item of items) {
		if (item.runId) {
			const existing = grouped.get(item.runId);
			if (existing) {
				existing.push(item);
			} else {
				grouped.set(item.runId, [item]);
			}
		} else {
			ungrouped.push(item);
		}
	}

	const runs: RunGroup[] = [];
	for (const [runId, groupItems] of grouped) {
		groupItems.sort(stageSort);
		const startTimes = groupItems.map(i => i.start).filter(Boolean) as Date[];
		const endTimes = groupItems.map(i => i.end).filter(Boolean) as Date[];
		runs.push({
			runId,
			items: groupItems,
			earliestStart: startTimes.length ? new Date(Math.min(...startTimes.map(d => new Date(d).getTime()))) : undefined,
			latestEnd: endTimes.length ? new Date(Math.max(...endTimes.map(d => new Date(d).getTime()))) : undefined,
		});
	}

	// Newest first
	runs.sort((a, b) => {
		const aTime = a.earliestStart ? new Date(a.earliestStart).getTime() : 0;
		const bTime = b.earliestStart ? new Date(b.earliestStart).getTime() : 0;
		return bTime - aTime;
	});

	return { runs, ungrouped };
};

const isLoadStats = (stats: Api.ProcessedStatsTypes): stats is Api.LoadProcessedStats => 'loaded' in stats;

const formatJobId = (jobId: string) => {
	if (!jobId || jobId.length <= 12) return jobId;
	return `${jobId.slice(0, 6)}...${jobId.slice(-6)}`;
};

/**
 * Render a millisecond duration in human-friendly units.
 *  <1s → "832ms"
 *  <1m → "8.87s"
 *  <1h → "2m 14s"
 *  ≥1h → "1h 23m"
 */
const formatFriendlyDuration = (ms: number): string => {
	if (ms < 0) return '-';
	if (ms < 1000) return `${Math.round(ms)}ms`;
	if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;
	if (ms < 3_600_000) {
		const m = Math.floor(ms / 60_000);
		const s = Math.floor((ms % 60_000) / 1000);
		return s ? `${m}m ${s}s` : `${m}m`;
	}
	const h = Math.floor(ms / 3_600_000);
	const m = Math.floor((ms % 3_600_000) / 60_000);
	return m ? `${h}h ${m}m` : `${h}h`;
};

/** Parse the server-side duration string `HH:MM:SS.fffffff` into milliseconds. */
const parseServerDurationToMs = (raw: string): number => {
	const [hms, frac = '0'] = raw.split('.');
	const [h, m, s] = hms.split(':').map(Number);
	const fracMs = Math.round(Number.parseFloat(`0.${frac}`) * 1000);
	return ((h * 60 + m) * 60 + s) * 1000 + fracMs;
};

const formatStageDuration = (raw?: string) => {
	if (!raw) return '-';
	return formatFriendlyDuration(parseServerDurationToMs(raw));
};

const formatRunDuration = (start?: Date, end?: Date) => {
	if (!start || !end) return '-';
	return formatFriendlyDuration(new Date(end).getTime() - new Date(start).getTime());
};

const copyId = (id: string, label: string) => {
	copyToClipboard(id);
	toast.success(`${label} copied to clipboard`);
};

interface RunCardProps {
	run: RunGroup;
}

const RunCard = ({ run }: RunCardProps) => {
	const [isOpen, setIsOpen] = React.useState(false);

	const start = run.earliestStart ? moment(run.earliestStart).format('MMM DD, hh:mm a') : '-';
	const end = run.latestEnd ? moment(run.latestEnd).format('MMM DD, hh:mm a') : '-';
	const duration = formatRunDuration(run.earliestStart, run.latestEnd);

	// `force` and `modifiedSince` are written identically on every stage in a run, so the first stage is authoritative.
	const sample = run.items[0];
	const isFullSync = sample?.force === true;
	const modifiedSince = sample?.modifiedSince;

	// e.g. PreProcess+Extract+Transform+Load → "PETL", just Extract+Load → "EL"
	const stageLetters = run.items.map(i => stageLabelOf(i).charAt(0).toUpperCase()).join('');
	const stageNames = run.items.map(stageLabelOf).join(', ');

	const onCopyRunId = () => copyId(run.runId, 'Run ID');

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen}>
			<div className={css(styleSheet.runCard, isOpen && styleSheet.runCardOpen)}>
				<div className={css(styleSheet.runCardHeader)}>
					<CollapsibleTrigger className={css(styleSheet.runCardTrigger)}>
						<span
							className={css(styleSheet.runCardChevron, isOpen && styleSheet.runCardChevronOpen)}
							aria-hidden='true'
						>
							<DisclosureIcon type='chevron' />
						</span>
						<div className={css(styleSheet.runCardHeaderLine)}>
							<span className={css(styleSheet.stageBadge)} aria-label={`Stages: ${stageNames}`} title={stageNames}>
								{stageLetters}
							</span>
							{isFullSync ? (
								<span className={css(styleSheet.modeBadge, styleSheet.modeBadgeFull)}>Full Sync</span>
							) : modifiedSince ? (
								<SimpleTooltip
									title={`Synced data modified since ${moment(modifiedSince).format('MMM D, YYYY [at] h:mm a')}`}
								>
									<span
										className={css(
											styleSheet.modeBadge,
											styleSheet.modeBadgeIncremental,
											styleSheet.modeBadgeWithTooltip
										)}
									>
										Incremental Sync
										<span className={css(styleSheet.modeBadgeInfo)} aria-hidden='true'>
											<InfoIcon fillColor='currentColor' />
										</span>
									</span>
								</SimpleTooltip>
							) : (
								<span className={css(styleSheet.modeBadge, styleSheet.modeBadgeIncremental)}>Incremental Sync</span>
							)}
							<span className={css(styleSheet.runCardFieldGroup)}>
								<span className={css(styleSheet.runCardFieldLabel)} aria-hidden='true'>
									Started
								</span>
								<span className={css(styleSheet.runCardSummaryItem)} aria-label={`Started ${start}`}>
									{start}
								</span>
							</span>
							<span className={css(styleSheet.runCardFieldGroup)}>
								<span className={css(styleSheet.runCardFieldLabel)} aria-hidden='true'>
									Ended
								</span>
								<span className={css(styleSheet.runCardSummaryItem)} aria-label={`Ended ${end}`}>
									{end}
								</span>
							</span>
							<span className={css(styleSheet.runCardFieldGroup)}>
								<span className={css(styleSheet.runCardFieldLabel)} aria-hidden='true'>
									Duration
								</span>
								<span className={css(styleSheet.runCardSummaryItem)} aria-label={`Duration ${duration}`}>
									{duration}
								</span>
							</span>
						</div>
					</CollapsibleTrigger>
					<div className={css(styleSheet.runCardHeaderRight)}>
						<span className={css(styleSheet.runIdLabel)}>Run ID</span>
						<button
							type='button'
							className={css(styleSheet.copyButton)}
							onClick={onCopyRunId}
							aria-label={`Copy Run ID ${run.runId}`}
						>
							{formatJobId(run.runId)} <ClipboardIcon />
						</button>
					</div>
				</div>
				<CollapsiblePanel>
					<div className={css(styleSheet.runCardStages)}>
						<div className={css(styleSheet.runStageHeader)}>
							<span className={css(styleSheet.runStageHeaderCell, styleSheet.runStageLabel)}>Stage</span>
							<span className={css(styleSheet.runStageHeaderCell, styleSheet.runStageDuration)}>Duration</span>
							<span className={css(styleSheet.runStageHeaderCell, styleSheet.runStageTotal)}>Records</span>
						</div>
						{run.items.map(item => (
							<RunStageRow key={`stage-${item.jobId}`} item={item} />
						))}
					</div>
				</CollapsiblePanel>
			</div>
		</Collapsible>
	);
};

const RunStageRow = ({ item }: { item: Api.ProcessedStatsTypes }) => {
	const stageLabel = stageLabelOf(item);
	const duration = formatStageDuration(item.duration);
	const onCopy = () => copyId(item.jobId, 'Job ID');

	return (
		<div className={css(styleSheet.runStageRowGroup)}>
			<div className={css(styleSheet.runStageRow)}>
				<span className={css(styleSheet.runStageLabel)}>
					{stageLabel}
					<button
						type='button'
						className={css(styleSheet.runStageCopyJob)}
						onClick={onCopy}
						aria-label={`Copy Job ID ${item.jobId}`}
					>
						<ClipboardIcon />
					</button>
				</span>
				<span className={css(styleSheet.runStageDuration)}>{duration}</span>
				<span className={css(styleSheet.runStageTotal)}>{(item.total ?? 0).toLocaleString()} records</span>
			</div>
			{isLoadStats(item) ? (
				<div className={css(styleSheet.runStageBreakdown)}>
					<span>{item.loaded.toLocaleString()} loaded</span>
					<span className={css(styleSheet.runCardSeparator)}>·</span>
					<span>{item.archived.toLocaleString()} archived</span>
					<span className={css(styleSheet.runCardSeparator)}>·</span>
					<span>{item.skipped.toLocaleString()} skipped</span>
				</div>
			) : null}
		</div>
	);
};

export const RecentActivityView = ({
	impersonationContext,
	integrationProvider,
}: {
	impersonationContext: Api.IImpersonationContext;
	integrationProvider: Api.IntegrationProvider;
}) => {
	const [dateRange, setDateRange] = React.useState<DateRange | undefined>(undefined);
	const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
	const [isUngroupedOpen, setIsUngroupedOpen] = React.useState(false);

	// Draft states for popover (applied on OK click)
	const [draftDateRange, setDraftDateRange] = React.useState<DateRange | undefined>(undefined);

	const filterRequest: Api.IntegrationActivitiesFilterRequest = {
		startDate: dateRange?.from,
		endDate: dateRange?.to,
	};

	const isSearching = !!(dateRange?.from || dateRange?.to);

	const activitiesQuery = useInfiniteExternalIntegrationActivities({
		filterRequest,
		impersonationContext,
		integrationProvider,
		pageSize: 50,
	});

	const onPresetSelected = (days: number) => {
		const from = moment().subtract(days, 'days').startOf('day').toDate();
		const to = moment().endOf('day').toDate();
		setDraftDateRange({ from, to });
	};

	const onOkClick = () => {
		setDateRange(draftDateRange);
		setIsPopoverOpen(false);
	};

	const onCancelClick = () => {
		setDraftDateRange(dateRange);
		setIsPopoverOpen(false);
	};

	const onClearSearch = () => {
		setDateRange(undefined);
		setDraftDateRange(undefined);
	};

	const formatDateRange = () => {
		if (dateRange?.from && dateRange?.to) {
			return (
				<span>
					<span>{moment(dateRange.from).format(DATE_FORMAT)}</span>
					{' — '}
					<span>{moment(dateRange.to).format(DATE_FORMAT)}</span>
				</span>
			);
		}
		return 'Date Range';
	};

	// Handle query errors and show fallback message
	React.useEffect(() => {
		if (activitiesQuery.isError) {
			const error = activitiesQuery.error as any;
			const errorMessage =
				error?.systemMessage || error?.message || 'Failed to load activities. Please try again or clear filters.';
			toast.error(errorMessage);
			// Reset to no filters on error
			setDateRange(undefined);
		}
	}, [activitiesQuery.isError, activitiesQuery.error]);

	if (activitiesQuery.isPending) return <LoadingSpinner type='large' />;

	const items: Api.ProcessedStatsTypes[] =
		activitiesQuery.data?.pages?.flatMap((page: Api.IPagedCollection<Api.ProcessedStatsTypes>) => page.values) ?? [];

	const { runs, ungrouped } = partitionActivity(items);

	const renderUngroupedRow = (item: Api.ProcessedStatsTypes) => {
		const start = item.start ? moment(item.start).format('MMM DD, hh:mm a') : '-';
		const end = item.end ? moment(item.end).format('MMM DD, hh:mm a') : '-';
		const duration = formatStageDuration(item.duration);
		const loaded = isLoadStats(item) ? item.loaded : '-';
		const archived = isLoadStats(item) ? item.archived : '-';
		const skipped = isLoadStats(item) ? item.skipped : '-';

		return (
			<DataGridRow key={`ungrouped-${item.jobId}`}>
				<DataGridRowItem>{stageLabelOf(item)}</DataGridRowItem>
				<DataGridRowItem>
					<button
						type='button'
						className={css(styleSheet.copyButton)}
						onClick={() => copyId(item.jobId, 'Job ID')}
						aria-label={`Copy Job ID ${item.jobId}`}
					>
						{formatJobId(item.jobId)} <ClipboardIcon />
					</button>
				</DataGridRowItem>
				<DataGridRowItem>{start}</DataGridRowItem>
				<DataGridRowItem>{end}</DataGridRowItem>
				<DataGridRowItem>{duration}</DataGridRowItem>
				<DataGridRowItem>
					<h6 className={css(baseStyleSheet.truncateText, baseStyleSheet.m0)}>{item.total}</h6>
				</DataGridRowItem>
				<DataGridRowItem>{loaded}</DataGridRowItem>
				<DataGridRowItem>{archived}</DataGridRowItem>
				<DataGridRowItem>{skipped}</DataGridRowItem>
			</DataGridRow>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.filterContainer)}>
				<Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
					<PopoverTrigger asChild>
						<SelectButton>{formatDateRange()}</SelectButton>
					</PopoverTrigger>
					<PopoverContent align='start' side='bottom' styles={[styleSheet.popoverContent]}>
						<div className={css(styleSheet.dateRangeContainer)}>
							<div className={css(styleSheet.presetButtons)}>
								{DATE_RANGE_PRESETS.map(preset => (
									<Button
										key={`recent-activity-preset-${preset}`}
										onClick={() => onPresetSelected(preset)}
										size='sm'
										variant='secondary'
									>
										{`Last ${preset} days`}
									</Button>
								))}
							</div>
							<div className={css(styleSheet.calendarContainer)}>
								<Calendar
									mode='range'
									onSelect={setDraftDateRange}
									selected={draftDateRange}
									numberOfMonths={2}
									disabled={{ after: moment().toDate() }}
								/>
							</div>
						</div>
						<div className={css(styleSheet.buttonContainer)}>
							<Button onClick={onCancelClick} size='sm' variant='secondary'>
								Cancel
							</Button>
							<Button onClick={onOkClick} size='sm' variant='primary'>
								OK
							</Button>
						</div>
					</PopoverContent>
				</Popover>
				{isSearching && (
					<button type='button' className={css(styleSheet.clearSearch)} onClick={onClearSearch}>
						<span>Clear Search</span>
					</button>
				)}
			</div>

			{runs.length > 0 ? (
				<TooltipProvider>
					<section className={css(styleSheet.activitySection)}>
						<h3 className={css(styleSheet.sectionLabel)}>Runs</h3>
						<div className={css(styleSheet.runCardList)}>
							{runs.map(run => (
								<RunCard key={`run-${run.runId}`} run={run} />
							))}
						</div>
					</section>
				</TooltipProvider>
			) : null}

			{ungrouped.length > 0 ? (
				<section className={css(styleSheet.activitySection)}>
					<Collapsible open={isUngroupedOpen} onOpenChange={setIsUngroupedOpen}>
						<CollapsibleTrigger className={css(styleSheet.ungroupedSectionTrigger)}>
							<span
								className={css(styleSheet.runCardChevron, isUngroupedOpen && styleSheet.runCardChevronOpen)}
								aria-hidden='true'
							>
								<DisclosureIcon type='chevron' />
							</span>
							<span className={css(styleSheet.sectionLabel)}>Ungrouped Activity</span>
							<span className={css(styleSheet.ungroupedSectionCount)}>
								{ungrouped.length} {ungrouped.length === 1 ? 'record' : 'records'}
							</span>
						</CollapsibleTrigger>
						<CollapsiblePanel>
							<DataGrid styles={[styleSheet.tableOverrides, styleSheet.ungroupedTable]}>
								<DataGridColumns>
									<DataGridColumn sticky>Type</DataGridColumn>
									<DataGridColumn sticky>Job Id</DataGridColumn>
									<DataGridColumn sticky>Start Date</DataGridColumn>
									<DataGridColumn sticky>End Date</DataGridColumn>
									<DataGridColumn sticky>Duration</DataGridColumn>
									<DataGridColumn sticky>Total</DataGridColumn>
									<DataGridColumn sticky>Loaded</DataGridColumn>
									<DataGridColumn sticky>Archived</DataGridColumn>
									<DataGridColumn sticky>Skipped</DataGridColumn>
								</DataGridColumns>
								<DataGridRows>{ungrouped.map(renderUngroupedRow)}</DataGridRows>
							</DataGrid>
						</CollapsiblePanel>
					</Collapsible>
				</section>
			) : null}

			{activitiesQuery.isFetchingNextPage ? (
				<div className={css(baseStyleSheet.flexCenter, baseStyleSheet.mt4)}>
					<LoadingSpinner type='large' />
				</div>
			) : null}

			{!activitiesQuery.isFetchingNextPage && activitiesQuery.hasNextPage ? (
				<Waypoint bottomOffset='-200px' onEnter={() => activitiesQuery.fetchNextPage()} />
			) : null}

			{activitiesQuery.isSuccess && items.length === 0 ? (
				<div className={css(baseStyleSheet.flexCenter, baseStyleSheet.mt4)}>
					<p className={css(styleSheet.emptyState)}>No activities found</p>
				</div>
			) : null}
		</div>
	);
};
